import { ISubmissionFile } from '@publishing-engine/domain/models';
import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IFileUploaded } from 'src/components/FormField/common/types';

interface UseAttachmentSyncProps {
  sourceForm: UseFormReturn<any>;
  targetForm: UseFormReturn<any>;
  sourceField: string;
  targetField: string;
  transformToTarget?: (files: any[]) => any[];
  transformToSource?: (files: any[]) => any[];
}

/**
 * Custom hook that automatically syncs attachments bidirectionally between two forms
 * Transforms between IFileUploaded and ISubmissionFile formats by default
 * Supports custom transformation functions for different type combinations
 */
export const useAttachmentSync = ({
  sourceForm,
  targetForm,
  sourceField,
  targetField,
  transformToTarget,
  transformToSource
}: UseAttachmentSyncProps) => {
  const sourceAttachments =
    sourceForm.watch(sourceField, {
      mode: 'onChange',
      defaultValue: []
    }) ?? [];
  const targetAttachments =
    targetForm.watch(targetField, {
      mode: 'onChange',
      defaultValue: []
    }) ?? [];
  const lastSourceJson = useRef('');
  const lastTargetJson = useRef('');

  // Default transform IFileUploaded to ISubmissionFile
  const defaultTransformToTarget = (files: IFileUploaded[] = []) => {
    if (!Array.isArray(files)) return [];
    return files.map(file => ({
      id: file.fileId,
      referenceName: file.referenceName,
      name: file.name,
      extension: file.extension,
      url: file.url
    }));
  };

  // Default transform ISubmissionFile to IFileUploaded
  const defaultTransformToSource = (files: ISubmissionFile[]) => {
    if (!Array.isArray(files)) return [];
    return files?.map(file => ({
      fileId: file.id,
      referenceName: file.referenceName,
      name: file.name,
      extension: file.extension,
      url: file.url,
      isUploading: false,
      progress: 0
    }));
  };

  // Use custom transforms if provided, otherwise use defaults
  const toTargetFormat = transformToTarget || defaultTransformToTarget;
  const toSourceFormat = transformToSource || defaultTransformToSource;

  // When 1 form changes attachments, it syncs to the other form
  useEffect(() => {
    const sourceJson = JSON.stringify(sourceAttachments);
    const targetJson = JSON.stringify(targetAttachments);

    if (sourceJson !== lastSourceJson.current) {
      lastSourceJson.current = sourceJson;
      lastTargetJson.current = JSON.stringify(
        toTargetFormat(sourceAttachments)
      );
      targetForm.setValue(targetField, toTargetFormat(sourceAttachments));
    } else if (targetJson !== lastTargetJson.current) {
      lastTargetJson.current = targetJson;
      lastSourceJson.current = JSON.stringify(
        toSourceFormat(targetAttachments)
      );
      sourceForm.setValue(sourceField, toSourceFormat(targetAttachments));
    }
  }, [
    sourceAttachments,
    targetAttachments,
    sourceForm,
    targetForm,
    sourceField,
    targetField,
    toTargetFormat,
    toSourceFormat
  ]);
};
